import { useEffect, useRef, useState } from 'react';


export interface UseAutoRefreshConfig {
  /** Milisegundos entre dos refrescos automáticos. */
  intervalMs: number;
  /** Acción a ejecutar en cada ciclo. No necesita ser estable. */
  onRefresh: () => void;
  /** Aplaza el ciclo mientras sea `true` y lo dispara al volver a `false`. */
  paused?: boolean;
  /** Cuando es `false` no hay temporizador. */
  enabled?: boolean;
}

export interface UseAutoRefreshResult {
  /** `true` cuando venció un ciclo en pausa y el refresco quedó a la espera. */
  hasPendingRefresh: boolean;
}

// Ejecuta una acción de refresco cada `intervalMs`, aplazándola mientras `paused` esté activo.
export function useAutoRefresh({
  intervalMs,
  onRefresh,
  paused = false,
  enabled = true,
}: UseAutoRefreshConfig): UseAutoRefreshResult {
  const [hasPendingRefresh, setHasPendingRefresh] = useState(false);

  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  // Refs para que el intervalo no se reinicie al cambiar la acción o la pausa.
  const pausedRef = useRef(paused);
  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    if (!enabled) return;

    const timerId = window.setInterval(() => {
      if (pausedRef.current) {
        setHasPendingRefresh(true);
        return;
      }
      onRefreshRef.current();
    }, intervalMs);

    return () => window.clearInterval(timerId);
  }, [enabled, intervalMs]);

  // Ejecuta el ciclo aplazado al terminar la pausa.
  useEffect(() => {
    if (!enabled || paused || !hasPendingRefresh) return;
    setHasPendingRefresh(false);
    onRefreshRef.current();
  }, [enabled, paused, hasPendingRefresh]);

  return { hasPendingRefresh };
}

export default useAutoRefresh;
